import { store } from 'store';
import { clearFeatureInfoByggnader, getFeatureInfoByggnader } from 'store/featureInfoByggnaderSlice';
import { clearFeatureInfoInventeringar, getFeatureInfoInventeringar } from 'store/featureInfoInventeringarSlice';
import { clearClickedActiveFeatures, setActiveInteraction, setClickedActiveFeatures } from 'store/mapSlice';

import { InteractionTypeEnum, MapModeEnum } from 'enums';

import { MapBrowserEvent } from 'ol';
import Feature, { FeatureLike } from 'ol/Feature';
import Map from 'ol/Map';
import { Coordinate } from 'ol/coordinate';
import GeoJSON from 'ol/format/GeoJSON';
import { Geometry } from 'ol/geom';
import { fromExtent } from 'ol/geom/Polygon';
import { Tile } from 'ol/layer';
import { TileWMS } from 'ol/source';

import {
  drawDeleteVectorLayer,
  drawDeleteVectorSource,
  drawVectorSource,
  getFeaturesAtPixelFromActiveLayer,
  publiceradeAktiviteterSymbolerWMS,
  publiceradeAktiviteterSymbolerWMTS,
  publiceradeBebyggelseObjektSymbolerWMS,
  publiceradeBebyggelseObjektYtorWMS,
  publiceradeBebyggelseObjektYtorWMTS,
  publiceradeBebyggelseobjektSymbolerWMTS
} from './Layers';
import { epsg3006ExtentExtended } from './MapConfig';

import { LMByggnadSlice } from 'api/endpoints/LMByggnadSlice';

const getFeatureInfoUrlFromSource = (
  layer: Tile<TileWMS>,
  map: Map | undefined,
  coordinate: Coordinate
): string | undefined => {
  const resolution = map?.getView().getResolution();
  const source = layer.getSource();
  if (resolution && source) {
    return source.getFeatureInfoUrl(coordinate, resolution, 'EPSG:3006', {
      FEATURE_COUNT: '100',
      INFO_FORMAT: 'application/json'
    });
  }
};

const handleFeatureInfoSymbolerPopupClick = (event: MapBrowserEvent<MouseEvent>, map: Map | undefined): boolean => {
  const wmtsBebyggelseSymbolerRGBA = publiceradeBebyggelseobjektSymbolerWMTS.getData(event.pixel) as Uint8ClampedArray;
  const wmtsBebyggelseSymbolerHit = wmtsBebyggelseSymbolerRGBA && wmtsBebyggelseSymbolerRGBA[3] > 0;

  if (wmtsBebyggelseSymbolerHit) {
    const url = getFeatureInfoUrlFromSource(publiceradeBebyggelseObjektSymbolerWMS, map, event.coordinate);
    if (url) {
      store.dispatch(getFeatureInfoByggnader({ url, coordinate: event.coordinate }));
    }
  }

  const wmtsInventeringsomradenSymbolerRGBA = publiceradeAktiviteterSymbolerWMTS.getData(
    event.pixel
  ) as Uint8ClampedArray;
  const wmtsInventeringsomradenSymbolerHit =
    wmtsInventeringsomradenSymbolerRGBA && wmtsInventeringsomradenSymbolerRGBA[3] > 0;

  if (wmtsInventeringsomradenSymbolerHit) {
    const url = getFeatureInfoUrlFromSource(publiceradeAktiviteterSymbolerWMS, map, event.coordinate);
    if (url) {
      store.dispatch(getFeatureInfoInventeringar({ url, coordinate: event.coordinate }));
    }
  }

  return wmtsBebyggelseSymbolerHit || wmtsInventeringsomradenSymbolerHit;
};

const handleFeatureInfoYtaPopupClick = (event: MapBrowserEvent<MouseEvent>, map: Map | undefined): boolean => {
  const wmtsBebyggelseYtaRGBA = publiceradeBebyggelseObjektYtorWMTS.getData(event.pixel) as Uint8ClampedArray;
  const wmtsBebyggelseYtaHit = wmtsBebyggelseYtaRGBA && wmtsBebyggelseYtaRGBA[3] > 0;

  store.dispatch(clearFeatureInfoInventeringar(undefined));
  store.dispatch(clearFeatureInfoByggnader(undefined));

  if (wmtsBebyggelseYtaHit) {
    const url = getFeatureInfoUrlFromSource(publiceradeBebyggelseObjektYtorWMS, map, event.coordinate);
    if (url) {
      store.dispatch(getFeatureInfoByggnader({ url, coordinate: event.coordinate }));
    }
  }

  return wmtsBebyggelseYtaHit;
};

const handleEditGeometryClick = (event: MapBrowserEvent<MouseEvent>): void => {
  if (store.getState().map.activeInteractions.includes(InteractionTypeEnum.MODIFY_DELETE)) {
    if (drawDeleteVectorSource.getFeatures().length) {
      drawDeleteVectorLayer.getFeatures(event.pixel).then((features: FeatureLike[]) => {
        features.forEach((feature: FeatureLike) => {
          drawVectorSource.removeFeature(feature.get('featureToRemove'));
          drawDeleteVectorSource.removeFeature(feature as Feature<Geometry>);
        });

        if (!drawVectorSource.getFeatures().length) {
          store.dispatch(setActiveInteraction(InteractionTypeEnum.DRAW));
        }
      });
    }
  }
};

const handleActiveLayerClick = async (event: MapBrowserEvent<MouseEvent>, map: Map | undefined): Promise<boolean> => {
  const activeFeatureProperties = await getFeaturesAtPixelFromActiveLayer(event.pixel, map);

  if (activeFeatureProperties.length) {
    store.dispatch(
      setClickedActiveFeatures({ activeFeatures: activeFeatureProperties, activeFeaturesRequestedAt: event.coordinate })
    );
  } else {
    store.dispatch(clearClickedActiveFeatures());
  }

  return Promise.resolve(true);
};

const handleLmByggnadClickEnkelinventering = async (event: MapBrowserEvent<MouseEvent>): Promise<boolean> => {
  const enkelinventering = store.getState().aktivitet?.data?.enkelinventering;

  if (enkelinventering) {
    document.body.style.cursor = 'wait';
    await store.dispatch(
      LMByggnadSlice.endpoints?.searchLmByggnad.initiate(event.coordinate, {
        fixedCacheKey: 'lm-byggnad'
      })
    );
    document.body.style.cursor = 'auto';
    return Promise.resolve(true);
  }
  return Promise.resolve(false);
};

const handleLmByggnadClickInventering = async (event: MapBrowserEvent<MouseEvent>): Promise<boolean> => {
  const inventering = store.getState().aktivitet?.data?.inventering;

  if (inventering) {
    document.body.style.cursor = 'wait';
    const inventeringsomradeFeatures = new GeoJSON().readFeatures(inventering.inventeringsomrade);

    if (inventeringsomradeFeatures.some((feature) => feature.getGeometry()?.intersectsCoordinate(event.coordinate))) {
      await store.dispatch(
        LMByggnadSlice.endpoints?.searchLmByggnad.initiate(event.coordinate, {
          fixedCacheKey: 'lm-byggnad'
        })
      );
    }
    document.body.style.cursor = 'auto';
    return Promise.resolve(true);
  }
  return Promise.resolve(false);
};

export const handleClick = async (event: MapBrowserEvent<MouseEvent>, map: Map | undefined) => {
  const { mapMode } = store.getState().map;

  switch (mapMode) {
    case MapModeEnum.EDIT_GEOMETRY:
      handleEditGeometryClick(event);
      break;
    case MapModeEnum.INVENTERING_REGISTRATION:
    case MapModeEnum.INVENTERING_PRESENTATION:
    case MapModeEnum.ACTIVE_LAYERS_ONLY:
    case MapModeEnum.SEARCH:
    case MapModeEnum.UTKAST_REGISTRATION:
    case MapModeEnum.UTKAST_PRESENTATION:
    case MapModeEnum.SAMMANSATT_UTKAST_PRESENTATION:
      await handleActiveLayerClick(event, map);
      handleFeatureInfoSymbolerPopupClick(event, map);
      break;
    case MapModeEnum.ADD_UTKAST_TO_ENKELINVENTERING:
      store.dispatch(clearFeatureInfoInventeringar(undefined));
      store.dispatch(clearFeatureInfoByggnader(undefined));
      store.dispatch(LMByggnadSlice.util.resetApiState());
      if (handleFeatureInfoSymbolerPopupClick(event, map)) break;
      if (handleFeatureInfoYtaPopupClick(event, map)) break;
      if (!fromExtent(epsg3006ExtentExtended).intersectsCoordinate(event.coordinate)) break;
      if (await handleLmByggnadClickEnkelinventering(event)) break;
      break;
    case MapModeEnum.ADD_UTKAST_TO_INVENTERING:
      store.dispatch(clearFeatureInfoInventeringar(undefined));
      store.dispatch(clearFeatureInfoByggnader(undefined));
      store.dispatch(LMByggnadSlice.util.resetApiState());
      if (handleFeatureInfoSymbolerPopupClick(event, map)) break;
      if (handleFeatureInfoYtaPopupClick(event, map)) break;
      if (!fromExtent(epsg3006ExtentExtended).intersectsCoordinate(event.coordinate)) break;
      if (await handleLmByggnadClickInventering(event)) break;
      break;
    case MapModeEnum.ADD_UTKAST_TO_SAMMANSATT:
      await handleActiveLayerClick(event, map);
      if (handleFeatureInfoSymbolerPopupClick(event, map)) break;
      break;
    case MapModeEnum.DEFAULT:
      await handleActiveLayerClick(event, map);
      handleFeatureInfoSymbolerPopupClick(event, map);
      break;
  }
};
